import performanceNow from "performance-now";
import {readHRCounter, writeHRCounter} from './timestampCounter';

/**
 * Return a new HRTimestamp, which is the unix processor time to the microsecond
 *
 * OWA-1083: with `after` (the addedOn of the row this tab holds for a record), the tab's counter
 * is first raised to `after`, so the stamp follows that row even when this tab's clock is behind,
 * and it stays unique and in order with every other stamp of this tab.
 *
 * @param {number} [after] - a stamp the new one must follow
 * @returns {number} A new high-res timestamp for use in addedOn records
 */
function newHRTimestamp(after?: number | null): number {
    // Create a new timestamp (this is the old functionality)
    const pn = (performanceNow() / 1000) % 1;
    let newTS = (Date.now() + pn) * 1000;

    // Now ensure it is unique (sometimes things happen so fast even performanceNow() gives the same
    // value): the tab's counter, in sessionStorage (or memory where there is none)
    let oldTS = readHRCounter();

    // Raise the counter to the stamp this one must follow
    const floor = Number(after);
    if (Number.isFinite(floor) && floor > oldTS) oldTS = floor;

    // If the old timestamp is the same or newer than the new timestamp,
    // we add one to the old timestamp and make it the new timestamp.
    if (oldTS >= newTS) {
        newTS = oldTS + 1;
    }

    writeHRCounter(newTS);

    return newTS;
}

/**
 * Return hrt in seconds.  Generally used to count seconds.
 * @param {number} hrt
 * @returns {number}
 */
export function hrtToSeconds(hrt: number): number {
    return hrt / 1000000;
}

/**
 * Convert a high-res microsecond timestamp to a JavaScript Date. hrt is
 * microseconds since Unix epoch (see newHRTimestamp above); Date expects
 * milliseconds, so divide by 1000.
 *
 * Historical note: this function previously divided by 1e9, placing every
 * date a few hundred milliseconds after epoch ("12/31/69 06:26 PM" in
 * Eastern Time) for any modern hrt. Callers that worked around the bug by
 * passing hrt/1000 themselves will now double-divide — fix those at the
 * callsite. The codebase-wide convention is util.hrtToDate from lib/util.tsx,
 * which has always been correct.
 */
export function hrtToTimestamp(hrt: number): Date {
    return new Date(Math.round(hrt / 1000));
}

export default newHRTimestamp;